/**
 * /mission — Mission document.
 *
 * Read view by default. Founder / admin see an "Edit" button that
 * swaps in the MarkdownEditor; "Submit proposal" sends the new text
 * to /api/mission (gated by founder approval workflow upstream).
 *
 * The current mission text is rendered as a single markdown block —
 * Mission updates are rare, so we keep the surface intentionally minimal.
 */
import type { MissionProposal } from '@chaos/types/constitution';
import { missionProposalSchema } from '@chaos/types/constitution';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Compass, Loader2, Pencil, X } from 'lucide-react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MarkdownEditor } from '../../components/editor/MarkdownEditor';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToaster } from '../../components/ui/toast';
import { constitutionApi, constitutionKeys } from '../../lib/api/constitution';
import { useAuthStore } from '../../stores/auth-store';

function MissionRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canEdit = role === 'founder' || role === 'admin';
  const [editing, setEditing] = React.useState(false);

  const missionQ = useQuery({
    queryKey: constitutionKeys.mission('global'),
    queryFn: () => constitutionApi.getMission('global'),
    staleTime: 30_000,
  });
  const mission = missionQ.data;

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Compass className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Mission</h1>
          <p className="text-[12px] text-muted-foreground">
            The North Star statement injected into every agent prompt.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {mission?.version ? (
            <span className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">
              v{mission.version}
            </span>
          ) : null}
          {canEdit ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing((e) => !e)}
              className="h-7"
            >
              {editing ? (
                <>
                  <X className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Cancel
                </>
              ) : (
                <>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Edit
                </>
              )}
            </Button>
          ) : null}
        </div>
      </header>

      {editing && canEdit ? (
        <MissionEditor
          initial={{
            statement: mission?.statement ?? '',
            rationale: mission?.rationale ?? '',
          }}
          onDone={() => setEditing(false)}
        />
      ) : (
        <article className="rounded border border-border bg-background p-5">
          {missionQ.isLoading ? (
            <p className="text-[12px] text-muted-foreground">Loading mission…</p>
          ) : mission == null ? (
            <p className="text-[12px] text-muted-foreground">
              No mission has been ratified yet.
              {canEdit ? ' Click "Edit" to propose one.' : ''}
            </p>
          ) : (
            <div className="prose prose-sm max-w-none text-[14px] leading-relaxed dark:prose-invert">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{mission.statement}</ReactMarkdown>
              {mission.rationale ? (
                <>
                  <hr className="my-4 border-border" />
                  <h3 className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Rationale
                  </h3>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{mission.rationale}</ReactMarkdown>
                </>
              ) : null}
            </div>
          )}
        </article>
      )}

      {mission?.founderApprovedAt ? (
        <footer className="text-[11px] text-muted-foreground">
          Founder-approved {new Date(mission.founderApprovedAt).toISOString().slice(0, 10)}.
        </footer>
      ) : null}
    </section>
  );
}

interface MissionEditorProps {
  initial: { statement: string; rationale: string };
  onDone: () => void;
}

function MissionEditor({ initial, onDone }: MissionEditorProps) {
  const qc = useQueryClient();
  const { toast } = useToaster();
  const form = useForm<MissionProposal>({
    resolver: zodResolver(missionProposalSchema),
    defaultValues: {
      statement: initial.statement,
      rationale: initial.rationale,
      scope: 'global',
    },
  });

  const submit = useMutation({
    mutationFn: (input: MissionProposal) => constitutionApi.proposeMission(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({
        title: 'Mission proposal submitted',
        description: 'Awaiting founder approval workflow.',
      });
      onDone();
    },
    onError: (err: Error) => {
      toast({ title: 'Submit failed', description: err.message, variant: 'destructive' });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    submit.mutate(values);
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5 text-[12px]">
        <label htmlFor="mission-statement" className="font-medium text-foreground">
          Statement
        </label>
        <Controller
          control={form.control}
          name="statement"
          render={({ field }) => (
            <MarkdownEditor
              id="mission-statement"
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="CHAOS exists to give solo founders an AI workforce that runs itself."
              rows={10}
            />
          )}
        />
        {form.formState.errors.statement ? (
          <span className="text-[11px] text-destructive-foreground">
            {form.formState.errors.statement.message}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5 text-[12px]">
        <label htmlFor="mission-rationale" className="font-medium text-foreground">
          Rationale (optional)
        </label>
        <Input
          id="mission-rationale"
          placeholder="Why now? Why this framing?"
          {...form.register('rationale')}
          disabled={submit.isPending}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Submitting…
            </>
          ) : (
            'Submit proposal'
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={submit.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export const Route = createFileRoute('/_authenticated/mission')({
  component: MissionRoute,
});
